import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { NodesToJSX } from './rich-text-renderer';
import { type RichTextNode, RichTextTableRowSection } from '@cinemark/content-api-client';
import type { SerializedLexicalNode } from '@payloadcms/richtext-lexical/lexical';

type TableCellNode = SerializedLexicalNode & {
  children?: SerializedLexicalNode[];
  colSpan?: number;
  headerState?: number;
  rowSpan?: number;
  type: 'tablecell';
};

type TableRowNode = SerializedLexicalNode & {
  children?: TableCellNode[];
  section?: RichTextNode['section'];
  type: 'tablerow';
};

type TableNode = SerializedLexicalNode & {
  children?: TableRowNode[];
  type: 'table';
};

type RichTextTableConverterComponentProps = {
  node: TableNode | undefined;
  nodesToJSX: NodesToJSX;
};

type RichTextTableRowConverterComponentProps = {
  node: TableRowNode | undefined;
  nodesToJSX: NodesToJSX;
};

type RichTextTableCellConverterComponentProps = {
  forceHeader?: boolean;
  node: TableCellNode | undefined;
  nodesToJSX: NodesToJSX;
};

type TableSections = {
  body: TableRowNode[];
  foot: TableRowNode[];
  head: TableRowNode[];
};

const isHeaderCell = (headerState: number | undefined): boolean => {
  return headerState !== undefined && headerState > 0;
};

const isHeaderRow = (row: TableRowNode): boolean => {
  if (!row.children?.length) {
    return false;
  }

  return row.children
    .filter(child => child.type === 'tablecell')
    .every(cell => isHeaderCell(cell.headerState));
};

const getRowSection = (row: TableRowNode): 'body' | 'foot' | 'head' | null => {
  const normalizedSection = typeof row.section === 'string'
    ? row.section.trim().toLowerCase()
    : null;

  switch (normalizedSection) {
    case RichTextTableRowSection.Head:
      return 'head';
    case RichTextTableRowSection.Body:
      return 'body';
    case RichTextTableRowSection.Foot:
      return 'foot';
    default:
      return null;
  }
};

const groupTableSections = (tableNode: TableNode): TableSections => {
  const sections = {
    body: [] as TableRowNode[],
    foot: [] as TableRowNode[],
    head: [] as TableRowNode[],
  };
  let hasSeenBodyRow = false;

  tableNode.children?.forEach((row) => {
    const explicitSection = getRowSection(row);

    if (explicitSection === 'head') {
      sections.head.push(row);
      return;
    }

    if (explicitSection === 'body') {
      hasSeenBodyRow = true;
      sections.body.push(row);
      return;
    }

    if (explicitSection === 'foot') {
      sections.foot.push(row);
      return;
    }

    if (!hasSeenBodyRow && isHeaderRow(row)) {
      sections.head.push(row);
      return;
    }

    hasSeenBodyRow = true;
    sections.body.push(row);
  });

  return sections;
};

const renderRows = (nodesToJSX: NodesToJSX, rows: TableRowNode[]): React.ReactNode[] => {
  return rows.map((row, index) =>
    <RichTextTableRowConverterComponent key={`${row.type}-${index}`}
      node={row}
      nodesToJSX={nodesToJSX}
    />
  );
};

export const RichTextTableConverterComponent = ({ node, nodesToJSX }: RichTextTableConverterComponentProps): React.JSX.Element | null => {
  if (!node || !node.children?.length) return null;

  const { body, foot, head } = groupTableSections(node);
  const headChildren = renderRows(nodesToJSX, head);
  const bodyChildren = renderRows(nodesToJSX, body);
  const footChildren = renderRows(nodesToJSX, foot);

  return (
    <Table className="table-fixed"
      data-test="table-rich-text-container"
    >
      {head.length > 0 &&
        <TableHeader data-test="table-header-rich-text">{headChildren}</TableHeader>
      }
      {body.length > 0 &&
        <TableBody data-test="table-body-rich-text">{bodyChildren}</TableBody>
      }
      {foot.length > 0 &&
        <TableFooter className="bg-transparent border-t-0 font-normal [&>tr]:last:border-b-0"
          data-test="table-footer-rich-text"
        >
          {footChildren}
        </TableFooter>
      }
    </Table>
  );
};

export const RichTextTableRowConverterComponent = ({ node, nodesToJSX }: RichTextTableRowConverterComponentProps): React.JSX.Element | null => {
  if (!node || !node.children?.length) return null;

  const shouldRenderHeaderCells = getRowSection(node) === 'head' || isHeaderRow(node);
  const children = node.children.map((child, index) => {
    return <RichTextTableCellConverterComponent key={`tablecell-${index}`}
      forceHeader={shouldRenderHeaderCells}
      node={child}
      nodesToJSX={nodesToJSX}
    />;
  });

  return <TableRow data-test="table-row-rich-text">{children}</TableRow>;
};

export const RichTextTableCellConverterComponent = ({ forceHeader, node, nodesToJSX }: RichTextTableCellConverterComponentProps): React.JSX.Element | null => {
  if (!node) return null;

  const children = nodesToJSX({ nodes: node.children ?? [] });

  const sharedProps = {
    colSpan: node.colSpan || 1,
    'data-test': 'table-cell-rich-text',
    rowSpan: node.rowSpan || 1,
  };

  if (forceHeader || isHeaderCell(node.headerState)) {
    return <TableHead {...sharedProps}>{children}</TableHead>;
  }

  return <TableCell {...sharedProps}
    className="whitespace-normal"
  >
    {children}
  </TableCell>;
};
